/**
 * Hand off a PDF + recipients to the device's native email app.
 *
 * Strategy (in order):
 *   1. If the browser supports the Web Share API with files (modern iOS/Android,
 *      desktop Chrome/Edge), call `navigator.share({ files, title, text })`.
 *      The user gets the native share sheet → picks Mail → the PDF is attached.
 *      The recipient list is included in the `text` body because no current
 *      Web Share API exposes a "to" field.
 *   2. Otherwise, silently push the PDF straight to the Downloads folder via a
 *      classic anchor-tag download (no "Save As" picker), then open a `mailto:`
 *      URL with recipient + subject + body pre-filled. The user attaches the
 *      file manually from Downloads.
 *
 * Returns one of: "shared" | "mailto" | "cancelled". Callers can surface a
 * toast based on the result.
 */

const MAILTO_BODY_FALLBACK_NOTE =
  "\n\n(The PDF was just saved to your Downloads folder — please attach it before sending.)";

function buildMailto(recipients, subject, body) {
  const to = (recipients || []).filter(Boolean).join(",");
  const params = new URLSearchParams();
  if (subject) params.set("subject", subject);
  if (body) params.set("body", body);
  // URLSearchParams uses '+' for spaces, which mailto: clients tolerate but
  // some treat literally. Re-encode using %20 just to be safe.
  const qs = params.toString().replace(/\+/g, "%20");
  return `mailto:${encodeURIComponent(to).replace(/%2C/g, ",")}${qs ? `?${qs}` : ""}`;
}

function canShareFiles(file) {
  try {
    return (
      typeof navigator !== "undefined" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [file] })
    );
  } catch {
    return false;
  }
}

// Silent download — bypasses the File System Access picker that `savePdf` uses,
// so the mailto: dialog can open in the same user gesture instead of being
// blocked behind a second prompt. The file lands in the browser's default
// Downloads folder.
function downloadBlobSilently(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke a tick later — some browsers (Safari) abort the download if the
  // URL is revoked synchronously after click().
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/**
 * @param {Object} opts
 * @param {Blob} opts.blob       PDF blob to share.
 * @param {string} opts.filename Suggested filename (e.g. "vital_signs.pdf").
 * @param {string[]} opts.recipients Email addresses to pre-fill.
 * @param {string} opts.subject  Email subject line.
 * @param {string} [opts.body]   Optional email body.
 * @returns {Promise<"shared" | "mailto" | "cancelled">}
 */
export async function shareOrMailtoPdf({ blob, filename, recipients, subject, body = "" }) {
  // Wrap blob as a File so the Web Share API can attach it correctly.
  const file = new File([blob], filename, { type: "application/pdf" });

  // ---- Path 1: Native share sheet (best UX on mobile) ----
  if (canShareFiles(file)) {
    const recipientLine = recipients?.length
      ? `To: ${recipients.join(", ")}\n\n`
      : "";
    try {
      await navigator.share({
        files: [file],
        title: subject,
        text: `${recipientLine}${body}`.trim() || subject,
      });
      return "shared";
    } catch (e) {
      // User dismissed the share sheet — treat as cancelled, don't fall through
      // to mailto (would be jarring to see a second compose window open).
      if (e?.name === "AbortError") return "cancelled";
      // Anything else (NotAllowedError, e.g. share blocked by browser policy)
      // → fall through to mailto so the user still gets a usable path.
    }
  }

  // ---- Path 2: Mailto + silent download fallback ----
  downloadBlobSilently(blob, filename);
  const mailto = buildMailto(
    recipients,
    subject,
    body ? body + MAILTO_BODY_FALLBACK_NOTE : MAILTO_BODY_FALLBACK_NOTE.trim()
  );
  // Use window.location to honour the OS default mailto handler. window.open
  // can pop a blank tab on some desktop browsers.
  window.location.href = mailto;
  return "mailto";
}

